import { useState } from 'react';
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Info, Plus, Trash2, ChevronDown } from 'lucide-react';
import { RoutingCondition } from '../tabs/types';
import { validateFormula, getFormulaExamples } from './formulaValidator';
import { WorkflowConditionField, resolveWorkflowConditionField } from './workflowConditionFields';

interface ConditionBuilderProps {
  conditions: RoutingCondition[];
  setConditions: (conditions: RoutingCondition[]) => void;
  formula: string;
  setFormula: (formula: string) => void;
  conditionFields: WorkflowConditionField[];
  showFormula?: boolean;
}

export default function ConditionBuilder({
  conditions,
  setConditions,
  formula,
  setFormula,
  conditionFields,
  showFormula = true,
}: ConditionBuilderProps) {
  const [formulaError, setFormulaError] = useState('');

  const getNextLetter = (): string => {
    if (conditions.length === 0) return 'A';
    const lastLetter = conditions[conditions.length - 1].letter;
    return String.fromCharCode(lastLetter.charCodeAt(0) + 1);
  };

  const addCondition = () => {
    const newCondition: RoutingCondition = {
      id: Math.random().toString(36).substr(2, 9),
      letter: getNextLetter(),
      field: conditionFields[0]?.key || '',
      operator: 'equals',
      value: ''
    };
    setConditions([...conditions, newCondition]);
  };

  const removeCondition = (id: string) => {
    const filtered = conditions.filter(c => c.id !== id);
    // Reassign letters
    const reassigned = filtered.map((condition, index) => ({
      ...condition,
      letter: String.fromCharCode(65 + index) // A, B, C, ...
    }));
    setConditions(reassigned);
  };

  const updateCondition = (id: string, field: keyof RoutingCondition, value: string | string[]) => {
    setConditions(conditions.map(c =>
      c.id === id ? { ...c, [field]: value } : c
    ));
  };

  const getFieldType = (fieldName: string): string => {
    const field = resolveWorkflowConditionField(conditionFields, fieldName);
    return field?.type || 'text';
  };

  const getFieldOptions = (fieldName: string): string[] => {
    const field = resolveWorkflowConditionField(conditionFields, fieldName);
    return field?.options || [];
  };

  const getOperatorsForFieldType = (fieldType: string): Array<{ value: string; label: string }> => {
    switch (fieldType) {
      case 'number':
        return [
          { value: 'equals', label: 'equals' },
          { value: 'less_than', label: 'less than' },
          { value: 'greater_than', label: 'greater than' }
        ];
      case 'select':
      case 'dropdown':
      case 'radio':
        return [
          { value: 'equals', label: 'equals' },
          { value: 'in', label: 'in' }
        ];
      default:
        return [
          { value: 'equals', label: 'equals' },
          { value: 'contains', label: 'contains' }
        ];
    }
  };

  const handleFieldChange = (conditionId: string, newFieldName: string) => {
    const fieldType = getFieldType(newFieldName);
    const operators = getOperatorsForFieldType(fieldType);
    const defaultOperator = operators[0].value;
    
    setConditions(conditions.map(c => {
      if (c.id === conditionId) {
        return {
          ...c,
          field: newFieldName,
          operator: defaultOperator as any,
          value: ''
        };
      }
      return c;
    }));
  };

  const validateFormulaInput = () => {
    if (!showFormula) return true;
    
    const validLetters = conditions.map(c => c.letter);
    const result = validateFormula(formula, validLetters);
    
    if (!result.isValid) {
      setFormulaError(result.errors.join('; '));
      return false;
    }
    
    setFormulaError('');
    return true;
  };

  const getFieldLabel = (fieldName: string) => {
    const field = resolveWorkflowConditionField(conditionFields, fieldName);
    return field ? field.label : fieldName;
  };

  const getFieldSelectValue = (fieldName: string) => {
    return resolveWorkflowConditionField(conditionFields, fieldName)?.key || fieldName;
  };

  const getBadgeClassName = (field: WorkflowConditionField) => {
    switch (field.badgeType) {
      case 'question':
        return 'border-purple-500/30 bg-purple-500/10 text-purple-600 dark:text-purple-400';
      case 'ai_label':
        return 'border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400';
      case 'system':
        return 'border-slate-500/30 bg-slate-500/10 text-slate-600 dark:text-slate-300';
      case 'visitor_id':
        return 'border-blue-500/30 bg-blue-500/10 text-blue-600 dark:text-blue-400';
      case 'company_data':
        return 'border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400';
      case 'account':
        return 'border-fuchsia-500/30 bg-fuchsia-500/10 text-fuchsia-700 dark:text-fuchsia-400';
      default:
        return 'border-cyan-500/30 bg-cyan-500/10 text-cyan-700 dark:text-cyan-400';
    }
  };

  const fieldGroups = [
    { key: 'field', label: 'Fields', fields: conditionFields.filter((field) => field.badgeType === 'field') },
    { key: 'question', label: 'Questions', fields: conditionFields.filter((field) => field.badgeType === 'question') },
    { key: 'ai_label', label: 'AI Labels', fields: conditionFields.filter((field) => field.badgeType === 'ai_label') },
    { key: 'visitor_id', label: 'Visitor ID', fields: conditionFields.filter((field) => field.badgeType === 'visitor_id') },
    { key: 'company_data', label: 'Company Data', fields: conditionFields.filter((field) => field.badgeType === 'company_data') },
    { key: 'account', label: 'Account', fields: conditionFields.filter((field) => field.badgeType === 'account') },
    { key: 'system', label: 'System', fields: conditionFields.filter((field) => field.badgeType === 'system') },
  ].filter((group) => group.fields.length > 0);

  return (
    <div className="space-y-4">
      {/* Conditions Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Label>Conditions</Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addCondition}
            disabled={conditions.length >= 26 || conditionFields.length === 0}
          >
            <Plus className="mr-1.5 h-3.5 w-3.5" />
            Add condition
          </Button>
        </div>

        {conditionFields.length === 0 ? (
          <div className="rounded-lg border border-dashed p-6 text-center">
            <p className="text-sm text-muted-foreground">
              No visitor fields are available for conditions yet.
            </p>
          </div>
        ) : conditions.length === 0 ? (
          <div className="rounded-lg border border-dashed p-6 text-center">
            <p className="text-sm text-muted-foreground mb-4">
              No conditions added yet. Add conditions to create classification rules.
            </p>
            <Button type="button" variant="outline" onClick={addCondition}>
              <Plus className="mr-1.5 h-3.5 w-3.5" />
              Add first condition
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {conditions.map((condition) => {
              const fieldType = getFieldType(condition.field);
              const fieldOptions = getFieldOptions(condition.field);
              const operators = getOperatorsForFieldType(fieldType);
              const isDropdownField = ['select', 'dropdown', 'radio'].includes(fieldType);
              const isMultiSelect = condition.operator === 'in';

              return (
                <div key={condition.id} className="space-y-3 rounded-lg border bg-background p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-muted-foreground">
                      Condition {condition.letter}
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeCondition(condition.id)}
                      className="h-7 w-7 text-muted-foreground hover:text-destructive"
                      aria-label={`Remove condition ${condition.letter}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>

                  <div className="grid gap-2 @xl:grid-cols-[minmax(0,1.4fr)_minmax(8rem,0.7fr)_minmax(0,1fr)]">
                    <div className="space-y-1">
                      <span className="text-[11px] font-medium text-muted-foreground">Data</span>
                      <Select
                        value={getFieldSelectValue(condition.field)}
                        onValueChange={(value) => handleFieldChange(condition.id, value)}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {fieldGroups.map((group) => (
                            <div key={group.key}>
                              <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                                {group.label}
                              </div>
                              {group.fields.map((field) => (
                                <SelectItem key={field.key} value={field.key}>
                                  <div className="flex min-w-0 items-center gap-2">
                                    <span className="truncate">{field.label}</span>
                                    <Badge
                                      variant="outline"
                                      className={`shrink-0 px-1.5 py-0 text-[10px] font-medium ${getBadgeClassName(field)}`}
                                    >
                                      {field.badgeLabel}
                                    </Badge>
                                  </div>
                                </SelectItem>
                              ))}
                            </div>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-1">
                      <span className="text-[11px] font-medium text-muted-foreground">Operator</span>
                      <Select
                        value={condition.operator}
                        onValueChange={(value: any) => {
                          setConditions(conditions.map(c => {
                            if (c.id === condition.id) {
                              return {
                                ...c,
                                operator: value,
                                value: value === 'in' ? [] : ''
                              };
                            }
                            return c;
                          }));
                        }}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {operators.map((op) => (
                            <SelectItem key={op.value} value={op.value}>
                              {op.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-1">
                      <span className="text-[11px] font-medium text-muted-foreground">Value</span>
                      {isDropdownField && isMultiSelect ? (
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          role="combobox"
                          className="w-full justify-between"
                        >
                          {Array.isArray(condition.value) && condition.value.length > 0
                            ? `${condition.value.length} selected`
                            : 'Select values...'}
                          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[300px] p-0">
                        <div className="max-h-[300px] overflow-y-auto p-2">
                          {fieldOptions.map((option) => {
                            const isSelected = Array.isArray(condition.value) && condition.value.includes(option);
                            return (
                              <div
                                key={option}
                                className="flex items-center space-x-2 p-2 hover:bg-accent rounded-sm cursor-pointer"
                                onClick={() => {
                                  const currentValues = Array.isArray(condition.value) ? condition.value : [];
                                  if (currentValues.includes(option)) {
                                    updateCondition(condition.id, 'value', currentValues.filter(v => v !== option));
                                  } else {
                                    updateCondition(condition.id, 'value', [...currentValues, option]);
                                  }
                                }}
                              >
                                <Checkbox
                                  checked={isSelected}
                                  onCheckedChange={() => {}}
                                />
                                <span className="text-sm">{option}</span>
                              </div>
                            );
                          })}
                        </div>
                      </PopoverContent>
                    </Popover>
                      ) : isDropdownField ? (
                    <Select
                      value={typeof condition.value === 'string' ? condition.value : ''}
                      onValueChange={(value) => updateCondition(condition.id, 'value', value)}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select value..." />
                      </SelectTrigger>
                      <SelectContent>
                        {fieldOptions.map((option) => (
                          <SelectItem key={option} value={option}>
                            {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                      ) : (
                    <Input
                      type={fieldType === 'number' ? 'number' : 'text'}
                      value={typeof condition.value === 'string' ? condition.value : ''}
                      onChange={(e) => updateCondition(condition.id, 'value', e.target.value)}
                      placeholder="Value..."
                      className="w-full"
                    />
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Formula Section */}
      {showFormula && conditions.length > 0 && (
        <div className="space-y-2">
          <Label htmlFor="formula">Logical Formula</Label>
          <Input
            id="formula"
            value={formula}
            onChange={(e) => {
              setFormula(e.target.value);
              setFormulaError('');
            }}
            onBlur={validateFormulaInput}
            placeholder="e.g., (A AND B) OR C"
            className={formulaError ? 'border-red-500' : ''}
          />
          {formulaError && (
            <p className="text-sm text-red-500">{formulaError}</p>
          )}
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              <div className="space-y-1">
                <p>Use AND, OR, NOT operators with condition letters. Examples:</p>
                <ul className="list-disc list-inside text-xs space-y-1 mt-2">
                  {getFormulaExamples().slice(0, 4).map((example, i) => (
                    <li key={i}>{example}</li>
                  ))}
                </ul>
              </div>
            </AlertDescription>
          </Alert>
        </div>
      )}
    </div>
  );
}
